import type { AdditionalNodeContextFragment } from '@atlaskit/editor-common/types/selection';
import type { JSONNode } from '@atlaskit/editor-json-transformer/types';
import type { Attrs } from '@atlaskit/editor-prosemirror/model';

import type { ContextExtractorRegistry, NodeContextFragmentExtractor } from './types';

/**
 * A registry of context extractors for different node types
 */
export const createContextExtractorRegistry = (): ContextExtractorRegistry => {
	const contextExtractors: Map<string, NodeContextFragmentExtractor> = new Map();

	return {
		register: (nodeType: string, extractor: NodeContextFragmentExtractor) => {
			contextExtractors.set(nodeType, extractor);
		},
		get: (nodeType: string): NodeContextFragmentExtractor | undefined => {
			return contextExtractors.get(nodeType);
		},
	};
};

/**
 * Get additional context for a list of fragments
 * @param fragments - The fragments to get additional context for
 * @param contextExtractorRegistry - The registry of context extractors
 * @returns A record of additional context for the fragments
 */
export const getAdditionalContextForFragments = (
	fragments: JSONNode[],
	contextExtractorRegistry: ContextExtractorRegistry,
): Record<string, AdditionalNodeContextFragment> => {
	return fragments.reduce(
		(acc: Record<string, AdditionalNodeContextFragment>, fragment: JSONNode) => {
			const extractor = contextExtractorRegistry.get(fragment.type);
			const localId = (fragment.attrs as Attrs)?.localId;

			if (extractor && localId) {
				const contextFragment = extractor(fragment);

				if (contextFragment !== null) {
					acc[localId] = contextFragment;
				}
			}
			return acc;
		},
		{},
	);
};
